import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, Search, Clapperboard, Briefcase, MessageSquare, Crown } from "lucide-react";
import { Logo } from "@/components/Brand";
import { DiwaliBanner } from "@/components/DiwaliBanner";
import { ProCard } from "@/components/ProCard";
import { WhatsAppSupport, SupportLink } from "@/components/WhatsAppSupport";
import { AudienceStrip, AppPromo, OpenInAppBar } from "@/components/AppPromo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PRO_TYPES, ACCOUNT_LABEL, isListedPro } from "@/lib/constants";

const FEATURES = [
  { icon: Newspaper, title: "Community feed", text: "Share tax updates, deadlines and practice insights with peers.", span: "md:col-span-2" },
  { icon: Search, title: "Discover experts", text: "Find verified CAs, CSs and tax consultants by city and specialty." },
  { icon: Clapperboard, title: "Reels & videos", text: "Explain complex rules in short videos clients actually watch." },
  { icon: Briefcase, title: "Jobs & projects", text: "Post requirements or pick up filing, audit and compliance work.", span: "md:col-span-2" },
  { icon: MessageSquare, title: "Direct messaging", text: "Talk to clients and professionals without sharing numbers." },
  { icon: Crown, title: "Pro membership", text: "Verified badge, featured placement and priority leads." },
];

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur-md">
      <OpenInAppBar />
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-6">
        <Logo />
        <nav className="ml-6 hidden gap-6 text-sm font-medium text-slate-600 md:flex">
          <Link to="/discover" className="hover:text-tm-green" data-testid="landing-nav-discover">Discover</Link>
          <Link to="/reels" className="hover:text-tm-green" data-testid="landing-nav-reels">Reels</Link>
          <Link to="/jobs" className="hover:text-tm-green" data-testid="landing-nav-jobs">Jobs</Link>
          <Link to="/pricing" className="hover:text-tm-green" data-testid="landing-nav-pricing">Pricing</Link>
          <Link to="/about" className="hover:text-tm-green" data-testid="landing-nav-about">About us</Link>
        </nav>
        <div className="ml-auto flex gap-2">
          {user ? (
            <Button asChild className="rounded-full bg-tm-green hover:bg-tm-green-light" data-testid="landing-open-app-button"><Link to="/feed">Open Taxmandi</Link></Button>
          ) : (
            <>
              <Button variant="ghost" asChild data-testid="landing-login-button"><Link to="/login">Log in</Link></Button>
              <Button asChild className="rounded-full bg-tm-green hover:bg-tm-green-light" data-testid="landing-join-button"><Link to="/register?as=client">Join us</Link></Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-[1.1fr_1fr] lg:py-24">
      <div className="tm-fade-up">
        <p className="tm-eyebrow">India's network for tax professionals</p>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-tm-green sm:text-5xl lg:text-6xl">
          Where CAs, CSs and tax experts <span className="text-tm-gold">grow their practice.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg">Build your professional profile, share knowledge through posts and reels, win new clients and hire talent — all on Taxmandi.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="rounded-full bg-tm-green px-7 hover:bg-tm-green-light" data-testid="hero-join-professional-button"><Link to="/register">Join as a professional <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-tm-gold px-7 text-tm-green-light" data-testid="hero-join-client-button"><Link to="/register?as=client">Join as client / business</Link></Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-tm-green px-7 text-tm-green" data-testid="hero-find-professional-button"><Link to="/discover">Find a professional</Link></Button>
        </div>
        <div className="mt-10 flex flex-wrap gap-2">
          {PRO_TYPES.map((p) => <Link key={p} to={`/discover?type=${p}`} className="rounded-full border bg-white px-3.5 py-1.5 text-sm text-slate-700 transition-colors duration-200 hover:border-tm-gold hover:text-tm-green">{ACCOUNT_LABEL[p]}</Link>)}
          <a href="https://play.google.com/store/apps/details?id=com.taxmandi.app" target="_blank" rel="noreferrer" className="rounded-full bg-tm-green px-3.5 py-1.5 text-sm font-semibold text-white" data-testid="landing-play-store-link">Get the Android app</a>
        </div>
      </div>
      <div className="relative">
        <div className="absolute -left-6 -top-6 h-32 w-32 rounded-3xl bg-tm-gold/30" />
        <img src="/images/hero.jpg" alt="Chartered accountant advising a client" className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" />
        <div className="absolute -bottom-6 left-6 rounded-2xl bg-tm-green p-5 text-white shadow-xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-tm-gold">Built for</p>
          <p className="mt-1 font-display text-lg font-bold">CA · CS · CMA · Tax advisors</p>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-16">
      <p className="tm-eyebrow">Everything in one place</p>
      <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold text-tm-green sm:text-3xl">The complete toolkit for modern tax practice</h2>
      <div className="mt-10 grid gap-4 md:grid-cols-4">
        {FEATURES.map(({ icon: Icon, title, text, span }, i) => (
          <div key={title} className={`tm-card tm-lift p-7 tm-fade-up ${span || ""} ${i === 0 ? "bg-tm-green text-white border-tm-green" : ""}`} style={{ animationDelay: `${i * 70}ms` }}>
            <Icon className={`h-6 w-6 ${i === 0 ? "text-tm-gold" : "text-tm-green-light"}`} />
            <h3 className={`mt-5 text-lg font-bold ${i === 0 ? "text-white" : "text-tm-green"}`}>{title}</h3>
            <p className={`mt-2 text-sm ${i === 0 ? "text-white/75" : "text-slate-600"}`}>{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturedPros() {
  const [pros, setPros] = useState([]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setPros(data.filter(isListedPro).slice(0, 4))).catch(() => {});
  }, []);
  if (!pros.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-6 py-16">
      <div className="flex items-end justify-between gap-4">
        <div><p className="tm-eyebrow">Featured professionals</p><h2 className="mt-3 font-display text-2xl font-bold text-tm-green sm:text-3xl">Meet experts on Taxmandi</h2></div>
        <Link to="/discover" className="text-sm font-semibold text-tm-green-light hover:underline" data-testid="landing-see-all-pros">See all</Link>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{pros.map((p) => <ProCard key={p.id} pro={p} />)}</div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-tm-sand">
      <Nav />
      <Hero />
      <AudienceStrip />
      <Features />
      <section className="mx-auto max-w-7xl px-6 py-8"><DiwaliBanner /></section>
      <FeaturedPros />
      <WhatsAppSupport />
      <AppPromo />
      <footer className="mt-12 bg-tm-dark py-12 text-white/70">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 md:flex-row md:items-center md:justify-between">
          <Logo dark />
          <Link to="/about" className="text-sm text-white/80 hover:underline" data-testid="footer-about-link">About us</Link>
          <SupportLink className="text-sm font-semibold text-[#25D366] hover:underline" />
          <p className="text-sm">© {new Date().getFullYear()} Taxmandi.com · Made for India's tax community</p>
        </div>
      </footer>
    </div>
  );
}
